import React, { FunctionComponent, useState, useEffect, useCallback } from "react";
import { InputNumber, InputNumberValueChangeParams } from "primereact/inputnumber";
import { T } from "services/lang/LanguageService";
import { Chips } from "primereact/chips";
import { MultiSelect } from "primereact/multiselect";
import TimeUnitDropdown from "components/basic/dropdowns/time-unit-dropdown/TimeUnitDropdown";
import { DropdownChangeParams } from "primereact/dropdown";
import { DashboardEditorProps } from "../../ComponentType";
import { LagLeadLoadingConfig } from "../charts/interfaces";
import { DefaultDatasetLoadingConf } from "../charts/LoadConfigInputs";
import { HiddenColumns } from "./AlertFan";
import { AlertFanEditorProps } from "./AlertTabMain";

const defaultAlertFanConfig: AlertFanEditorProps = {
  itemsPerPage: undefined,
  updateFrequency: undefined,
  topicTags: undefined,
  itemTags: undefined,
  loadConf: DefaultDatasetLoadingConf,
  hiddenColumns: [],
};

// Generate options from the enum
const hiddenColumnsOptions = Object.entries(HiddenColumns).map(([key, value]) => ({
  label: value,
  value,
}));

function AlertFanEditor({ config, onConfigChange }: DashboardEditorProps<AlertFanEditorProps>) {
  let conf = config ?? defaultAlertFanConfig;

  if (Object.keys(conf).length === 0) {
    conf = defaultAlertFanConfig;
  }

  const [hiddenColumns, setHiddenColumns] = useState<string[]>(conf.hiddenColumns || []);
  const [currentConfig, setCurrentConfig] = useState<AlertFanEditorProps>(conf);

  useEffect(() => {
    onConfigChange(currentConfig);
  }, [currentConfig]);

  const setConfigPropertyState = (property: keyof AlertFanEditorProps, value: any) => {
    setCurrentConfig(prevState => ({ ...prevState, [property]: value }));
  };

  const handleConfigChange = (e: any): void => {
    setConfigPropertyState(e.target.id as keyof AlertFanEditorProps, e.value);
  };

  const handleLoadConfDetailsChange = useCallback(
    (e: InputNumberValueChangeParams | DropdownChangeParams) => {
      const key = e.target.id as keyof LagLeadLoadingConfig;
      const { value } = e.target;

      setCurrentConfig(prev => ({
        ...prev,
        loadConf: { ...prev.loadConf, [key]: value } as LagLeadLoadingConfig,
      }));
    },
    []
  );

  const handleHiddenColumnsChange = (e: { value: string[] }) => {
    const newHiddenColumns = e.value;
    setHiddenColumns(newHiddenColumns);
    setConfigPropertyState("hiddenColumns", newHiddenColumns);
  };

  return (
    <div className="flex flex-column">
      <div className="grid">
        <div className="col-4 flex flex-column gap-2">
          <label htmlFor="itemsPerPage">{T("component.items-per-page")}</label>
          <InputNumber
            id="itemsPerPage"
            value={currentConfig.itemsPerPage}
            onValueChange={handleConfigChange}
          />
        </div>
        <div className="col-4 flex flex-column gap-2">
          <label htmlFor="updateFrequency">{T("load-conf.update-frequency")}</label>
          <InputNumber
            id="updateFrequency"
            value={currentConfig.updateFrequency}
            onValueChange={handleConfigChange}
          />
        </div>
        <div className="col-4 flex flex-column gap-2">
          <label htmlFor="hiddenColumns">Hidden columns</label>
          <MultiSelect
            placeholder={T("action.select-columns")}
            value={hiddenColumns}
            options={hiddenColumnsOptions}
            onChange={handleHiddenColumnsChange}
            showClear
            disabled={false}
            maxSelectedLabels={1}
          />
        </div>
        <div className="col-6 flex flex-column gap-2">
          <label htmlFor="topicTags">{T("word.topic-tags")}</label>
          <Chips id="topicTags" value={currentConfig.topicTags} onChange={handleConfigChange} />
        </div>
        <div className="col-6 flex flex-column gap-2">
          <label htmlFor="itemTags">{T("word.item-tags")}</label>
          <Chips id="itemTags" value={currentConfig.itemTags} onChange={handleConfigChange} />
        </div>
      </div>
      <div>
        <div className="flex flex-column">
          <div className="flex flex-row">
            <div className="col-4 gap-2 flex flex-column justify-content-center align-items-center">
              <label htmlFor="lag">{T("load-conf.lag")}</label>
              <div className="p-inputgroup">
                <InputNumber
                  id="lag"
                  value={currentConfig.loadConf?.lag}
                  onValueChange={handleLoadConfDetailsChange}
                />
                <TimeUnitDropdown
                  id="lagUnit"
                  className="p-inputgroup-addon p-0"
                  value={currentConfig.loadConf?.lagUnit || ""}
                  onChange={handleLoadConfDetailsChange}
                />
              </div>
            </div>
            <div className="col-4 gap-2 flex flex-column justify-content-center align-items-center">
              <label htmlFor="lead">{T("load-conf.lead")}</label>
              <div className="p-inputgroup">
                <InputNumber
                  id="lead"
                  value={currentConfig.loadConf?.lead}
                  onValueChange={handleLoadConfDetailsChange}
                />
                <TimeUnitDropdown
                  id="leadUnit"
                  className="p-inputgroup-addon p-0"
                  value={currentConfig.loadConf?.leadUnit || ""}
                  onChange={handleLoadConfDetailsChange}
                />
              </div>
            </div>
            <div className="col-4 gap-2 flex flex-column justify-content-center align-items-center">
              <label htmlFor="alignUnit">{T("load-conf.align-unit")}</label>
              <TimeUnitDropdown
                id="alignUnit"
                className="w-full"
                value={currentConfig.loadConf?.alignUnit || ""}
                onChange={handleLoadConfDetailsChange}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AlertFanEditor as FunctionComponent;
